import { ReactNode, useEffect, useState, useContext, useRef } from "react";
import { Badge, Button, Col, Nav, NavItem, NavLink, Row, Table, UncontrolledTooltip } from "reactstrap";
import Confirm from "../../common/Confirm";
import FilterBadge from "../../common/FilterBadge";
import RefreshButton from "../../common/RefreshButton";
import FilterInput from "../../common/FilterInput";
import { isMultiValue, KaSelect, KaSelectOption, toKaSelectOptions } from "../../common/KaSelect";
import useDisplayMessage from "../../common/lib/hooks/useDisplayMessage";
import KaAlert from "../../common/lib/KaAlert";
import OverflowTooltip from "../../common/OverflowTooltip";
import { adminFacingErrorMessage } from "../../common/userFacingMessages/userFacingMessages";
import useGetSiteSettings from "../../requests/useGetSiteSettings";
import { StagedOrderDetails, StagedOrderStep, useGetStagedOrders } from "../../requests/useGetStagedOrders";
import useLoadingCompleted from "../../requests/useLoadingCompleted";
import { useRemoveStagedOrder } from "../../requests/useRemoveStagedOrder";
import { useCompleteStagedOrderWithoutSignature } from "../../requests/useCompleteStagedOrderWithoutSignature";
import { BaySettings } from "./BayDirections/BayDirections";
import ReassignBayModal from "./ReassignBayModal";
import StagedOrderDetailsModal from "./StagedOrderDetailsModal";
import StagedOrderFilterModal from "./StagedOrderFilterModal";
import useLocalStorage from "../../common/lib/hooks/useLocalStorage";
import useInterval from "../../common/lib/hooks/useInterval";
import { SidebarContext } from "@ka/webcomponents/nav";
import { DefaultError, DefaultLoader } from "../../common/lib/MultiQueryView";

export type FilterSettings = {
  bay: string;
  stagedOrderStep: string;
}

export enum SelectableColumns {
  ReleaseNumber = "Release #",
  DriverEmail = "Driver Email",
  DriverNumber = "Driver #",
  DriverName = "Driver Name",
  Transport = "Transport",
  Customer = "Customer",
  Products = "Products",
  Amount = "Amount",
  RequestedQuantity = "Requested Quantity",
  TareWeight = "Tare Weight",
  Status = "Status",
  Bay = "Bay",
  Destination = "Destination",
  Created = "Created",
}

const StagedOrders = () => {
  const [stagedOrders, setStagedOrders] = useState<StagedOrderDetails[] | undefined>(undefined);

  const getStagedOrders = useGetStagedOrders({ onSuccess: setStagedOrders });
  const getBaySettings = useGetSiteSettings<BaySettings>();

  useInterval(() => getStagedOrders.query(), 30000, false);

  if (getStagedOrders.isError || getBaySettings.isError)
    return DefaultError(getStagedOrders.error ?? getBaySettings.error!);

  if (stagedOrders === undefined || getBaySettings.isLoading) return DefaultLoader;

  return <StagedOrdersContent
    stagedOrders={stagedOrders}
    baySettings={getBaySettings.data!}
    refreshStagedOrders={() => getStagedOrders.query()}
    isRefreshing={getStagedOrders.isLoading} />
}

interface StagedOrdersContentProps {
  stagedOrders: StagedOrderDetails[];
  baySettings: BaySettings;
  refreshStagedOrders: () => void;
  isRefreshing: boolean;
}

const StagedOrdersContent = (props: StagedOrdersContentProps) => {
  const displayMessage = useDisplayMessage();
  const [loaderViewActive, setLoaderViewActive] = useLocalStorage<boolean>("stagedOrders-loaderViewActive", false);
  const { isCollapsed, setCollapsed } = useContext(SidebarContext);
  const collapsedBeforeLoaderView = useRef(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [stagedOrderToReassignBay, setStagedOrderToReassignBay] = useState<StagedOrderDetails | undefined>(undefined);

  const [selectedColumns, setSelectedColumns] = useLocalStorage<string[]>("stagedOrders-selectedColumns", Object.values(SelectableColumns).map(s => s.toString()));

  const [filters, setFilters] = useLocalStorage<FilterSettings>("stagedOrders-filters", {
    bay: '',
    stagedOrderStep: '',
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) =>
    setFilters({ ...filters, ...newFilters });

  const changeView = (activateLoaderView: boolean) => {
    if (activateLoaderView) {
      collapsedBeforeLoaderView.current = isCollapsed;
      setCollapsed(true);
    } else {
      setCollapsed(collapsedBeforeLoaderView.current);
    }

    setLoaderViewActive(activateLoaderView);
    props.refreshStagedOrders();
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 600);

    return () => clearTimeout(handler);
  }, [search]);

  const stagedOrderStepOptions = toKaSelectOptions(Object.values(StagedOrderStep).map(s => ({ label: s.replace(/([A-Z])/g, ' $1').trim(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );
  const filterStep = stagedOrderStepOptions.find((option) => option.value === filters.stagedOrderStep);

  const bayOptions = toKaSelectOptions(props.baySettings.bays,
    "id",
    "name",
    { sortByLabel: true }
  );
  const filterBay = bayOptions.find((option) => option.value === filters.bay);

  const stagedOrdersFiltered = props.stagedOrders.filter((so) =>
    (so.orderNumber.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      so.orderNumbers.some(o => o.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
      (so.releaseNumber?.toLowerCase().includes(debouncedSearch.toLowerCase()) ?? false) ||
      so.customers.some(c => c.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
      so.transport.toLowerCase().includes(debouncedSearch.toLowerCase())) &&
    (filters.bay ? so.bay === filterBay?.label : true) &&
    (filters.stagedOrderStep ? so.step === filterStep?.value : true)
  );

  const selectableColumnOptions = toKaSelectOptions(Object.values(SelectableColumns).map<KaSelectOption>(s => ({ label: s.toString(), value: s.toString() })),
    "value",
    "label",
    { sortByLabel: false }
  );

  return <>
    {stagedOrderToReassignBay && <ReassignBayModal
      stagedOrder={stagedOrderToReassignBay}
      onSuccess={(m) => displayMessage.success(m)}
      onError={(m) => displayMessage.fail(m)}
      onClose={() => {
        setStagedOrderToReassignBay(undefined);
        props.refreshStagedOrders();
      }} />}
    <div className="d-flex justify-content-between">
      <h2>Staged Orders</h2>
      <div>
        <FilterBadge
          label="Status"
          value={filterStep?.label}
          onClose={() => updateFilters({ stagedOrderStep: '' })} />
        <FilterBadge
          label="Bay"
          value={filterBay?.label}
          onClose={() => updateFilters({ bay: '' })} />
        <RefreshButton
          spinning={props.isRefreshing}
          onRefresh={props.refreshStagedOrders} />
      </div>
    </div>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <Row>
      <Col className="d-flex justify-content-between">
        <form className="col col-lg-5 my-auto mx-3">
          <Row>
            <Col className="col-auto px-0 m-auto flex-fill">
              <FilterInput placeholder='Search orders, customers, transport...' value={search} onFilterTextChange={setSearch} />
            </Col>
            <Col className="col-auto px-2">
              <Button className="p-0"
                color="link"
                data-testid="show-filter-modal"
                onClick={() => setShowFilterModal(true)}>
                <i className="bi-filter" style={{ fontSize: '2rem' }} />
              </Button>
            </Col>
          </Row>
        </form>
        <Col className="col-auto">
          <Row>
            <Col className="col-auto m-auto">
              <KaSelect
                placeholder="Select columns"
                options={selectableColumnOptions}
                isClearable={false}
                controlShouldRenderValue={false}
                closeMenuOnSelect={false}
                isMulti={true}
                hideSelectedOptions={false}
                onChange={(valueObject) => {
                  if (isMultiValue(valueObject)) { setSelectedColumns(valueObject?.map(p => p.value)) }
                }}
                value={selectableColumnOptions.filter((option) => selectedColumns.includes(option.value))} />
            </Col>
            <Col className="col-auto">
              <Nav tabs>
                <NavItem>
                  <NavLink data-testid='officeView' active={!loaderViewActive} onClick={() => changeView(false)} className="p-1 pt-2" style={{ height: "69px", paddingBottom: "8px", cursor: "pointer" }}>
                    <img alt="officeViewButton" className="nav-icon" style={{ height: "55px" }} src="../../../images/svg/office_view_icon.svg" />
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink data-testid='loaderView' active={loaderViewActive} onClick={() => changeView(true)} className="p-1 px-3" style={{ height: "69px", paddingBottom: "12px", cursor: "pointer" }}>
                    <img alt="loaderViewButton" className="nav-icon" style={{ height: "60px" }} src="../../../images/svg/loader_view_icon.svg" />
                  </NavLink>
                </NavItem>
              </Nav>
            </Col>
          </Row>
        </Col>
      </Col>
    </Row>
    <div className="mt-1">
      {!loaderViewActive
        ? <OfficeView
          displayMessageSuccess={(m) => displayMessage.success(m)}
          displayMessageFail={(m) => displayMessage.fail(m)}
          refreshStagedOrders={props.refreshStagedOrders}
          stagedOrders={stagedOrdersFiltered}
          selectedColumns={selectedColumns}
          onReassignBay={setStagedOrderToReassignBay} />
        : <LoaderView
          displayMessageSuccess={(m) => displayMessage.success(m)}
          displayMessageFail={(m) => displayMessage.fail(m)}
          refreshStagedOrders={props.refreshStagedOrders}
          stagedOrders={stagedOrdersFiltered}
          selectedColumns={selectedColumns} />}
    </div>
    {showFilterModal && <StagedOrderFilterModal
      bayOptions={bayOptions}
      stagedOrderStepOptions={stagedOrderStepOptions}
      filters={filters}
      updateFilters={updateFilters}
      toggle={() => setShowFilterModal(false)} />}
  </>
}

interface OfficeViewProps {
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  refreshStagedOrders: () => void;
  stagedOrders: StagedOrderDetails[];
  selectedColumns: string[];
  onReassignBay: (so: StagedOrderDetails) => void;
}

const OfficeView = (props: OfficeViewProps) => {
  const [stagedOrderDetails, setStagedOrderDetails] = useState<StagedOrderDetails | undefined>(undefined);
  const [stagedOrderToRemove, setStagedOrderToRemove] = useState<StagedOrderDetails | undefined>(undefined);
  const [stagedOrderToCompleteWithoutSignature, setStagedOrderToCompleteWithoutSignature] = useState<StagedOrderDetails | undefined>(undefined);

  const removeStagedOrder = useRemoveStagedOrder(stagedOrderToRemove?.id ?? "", {
    onSuccess: () => {
      props.displayMessageSuccess(`Staged order for order number ${stagedOrderToRemove?.orderNumber} successfully removed`);
      setStagedOrderToRemove(undefined);
      props.refreshStagedOrders();
    },
    onError: (err) => {
      setStagedOrderToRemove(undefined);
      props.displayMessageFail(adminFacingErrorMessage(err))
    }
  });

  const completeStagedOrderWithoutSignature = useCompleteStagedOrderWithoutSignature(stagedOrderToCompleteWithoutSignature?.id ?? "", {
    onSuccess: () => {
      props.displayMessageSuccess(`A ticket for Staged Order ${stagedOrderToCompleteWithoutSignature?.orderNumber} will be printed without a signature. Please retrieve it from the printer.`);
      setStagedOrderToCompleteWithoutSignature(undefined);
      props.refreshStagedOrders();
    },
    onError: (err) => {
      setStagedOrderToCompleteWithoutSignature(undefined);
      props.displayMessageFail(adminFacingErrorMessage(err))
    }
  });

  return <>
    {stagedOrderDetails && <StagedOrderDetailsModal
      stagedOrder={stagedOrderDetails}
      onClose={() => setStagedOrderDetails(undefined)} />}
    {stagedOrderToRemove && <Confirm
      visible={!!stagedOrderToRemove}
      title={"Remove Staged Order"}
      body={`Are you sure you want to remove Staged Order for Order ${stagedOrderToRemove.orderNumber}?`}
      loading={removeStagedOrder.isLoading}
      onConfirm={() => removeStagedOrder.request({})}
      onDismiss={() => setStagedOrderToRemove(undefined)} />}
    {stagedOrderToCompleteWithoutSignature && <Confirm
      visible={!!stagedOrderToCompleteWithoutSignature}
      title={"Complete Staged Order without Signature"}
      body={`Are you sure you want to complete Staged Order ${stagedOrderToCompleteWithoutSignature.orderNumber} without collecting a signature?`}
      loading={completeStagedOrderWithoutSignature.isLoading}
      onConfirm={() => completeStagedOrderWithoutSignature.request({})}
      onDismiss={() => setStagedOrderToCompleteWithoutSignature(undefined)} />}
    <Table responsive className="p-3 mb-3">
      <thead>
        <tr className="p-1 mb-3 bg-primary bg-opacity-10">
          <th>Order #</th>
          {props.selectedColumns.includes(SelectableColumns.ReleaseNumber) && <th>Release #</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <th>Driver Email</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <th>Driver #</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverName) && <th>Driver Name</th>}
          {props.selectedColumns.includes(SelectableColumns.Transport) && <th>Transport</th>}
          {props.selectedColumns.includes(SelectableColumns.Customer) && <th>Customer</th>}
          {props.selectedColumns.includes(SelectableColumns.Products) && <th>Products</th>}
          {props.selectedColumns.includes(SelectableColumns.Amount) && <th>Amount</th>}
          {props.selectedColumns.includes(SelectableColumns.RequestedQuantity) && <th>Requested Quantity</th>}
          {props.selectedColumns.includes(SelectableColumns.TareWeight) && <th>Tare Weight</th>}
          {props.selectedColumns.includes(SelectableColumns.Status) && <th>Status</th>}
          {props.selectedColumns.includes(SelectableColumns.Bay) && <th>Bay</th>}
          {props.selectedColumns.includes(SelectableColumns.Destination) && <th>Destination</th>}
          {props.selectedColumns.includes(SelectableColumns.Created) && <th>Created</th>}
          <th className="col-1">Actions</th>
        </tr>
      </thead>
      <tbody>
        {props.stagedOrders.sort(alphabeticallyByName).map((so) =>
          <tr key={so.id} className="border-bottom border-1">
            <td>
              <div className="d-flex justify-content-between">
                <OverflowTooltip text={so.orderNumber + " "} />
                {so.orderNumbers.length > 0 ? <><i
                  className="bi-info-circle ps-1"
                  id={`info-${so.id}`} />
                  <UncontrolledTooltip target={`info-${so.id}`} >
                    {`Orders: ${so.orderNumbers.reduce((o1, o2) => o1 + ', ' + o2)}`}
                  </UncontrolledTooltip>
                </> : <></>}
              </div>
            </td>
            {props.selectedColumns.includes(SelectableColumns.ReleaseNumber) && <td><OverflowTooltip text={so.releaseNumber ?? ""} /></td>}
            {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <td><OverflowTooltip text={so.userEmail} /></td>}
            {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <td>{so.driverNumber}</td>}
            {props.selectedColumns.includes(SelectableColumns.DriverName) && <td><OverflowTooltip text={so.driverName} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Transport) && <td>{so.transport}</td>}
            {props.selectedColumns.includes(SelectableColumns.Customer) && <td><OverflowTooltip text={so.customers.reduce((c1, c2) => c1 + ', ' + c2)} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Products) && <td><OverflowTooltip text={so.products.reduce((p1, p2) => p1 + ', ' + p2)} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Amount) && <td className="text-nowrap">{so.availableToRequest + " lb"}</td>}
            {props.selectedColumns.includes(SelectableColumns.RequestedQuantity) && <td className="text-nowrap">{so.requestedQuantity + " lb"}</td>}
            {props.selectedColumns.includes(SelectableColumns.TareWeight) && <td className="text-nowrap">{so.tareWeight}</td>}
            {props.selectedColumns.includes(SelectableColumns.Status) && <td>{formatStagedOrderStep(so.step)}</td>}
            {props.selectedColumns.includes(SelectableColumns.Bay) && <td>{so.bay}</td>}
            {props.selectedColumns.includes(SelectableColumns.Destination) && <td><OverflowTooltip text={so.destinations.length > 0 ? so.destinations.reduce((d1, d2) => d1 + ', ' + d2) : ''} /></td>}
            {props.selectedColumns.includes(SelectableColumns.Created) && <td>{new Date(so.createdAt).toLocaleDateString()}</td>}
            <td>
              <div className="dropdown">
                <Button
                  className="dropdown-toggle btn-ghost-secondary"
                  color="link"
                  id="actionsMenuButton"
                  data-bs-toggle="dropdown"
                  data-bs-popper-config='{"strategy":"fixed"}'
                  aria-expanded="false"
                  style={{ marginTop: '3px' }}>
                  <strong>...</strong>
                </Button>
                <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                  <Button
                    className="btn btn-ghost-secondary d-block"
                    color="link"
                    onClick={() => setStagedOrderDetails(so)}>
                    View
                  </Button>
                  {!so.locked && so.step === StagedOrderStep.BayDirections && <Button
                    className="btn btn-ghost-secondary d-block"
                    color="link"
                    onClick={() => props.onReassignBay(so)}>
                    Reassign Bay
                  </Button>}
                  {so.step !== StagedOrderStep.Signature && so.step !== StagedOrderStep.TicketInstructions && <Button
                    className="btn btn-ghost-danger d-block"
                    color="link"
                    onClick={() => setStagedOrderToRemove(so)}>
                    Remove
                  </Button>}
                  {so.step === StagedOrderStep.Signature && <Button
                    className="btn btn-ghost-danger d-block"
                    color="link"
                    onClick={() => setStagedOrderToCompleteWithoutSignature(so)}>
                    Complete Without Signature
                  </Button>}
                </div>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </>
}

interface LoaderViewProps {
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  refreshStagedOrders: () => void;
  stagedOrders: StagedOrderDetails[];
  selectedColumns: string[];
}

const LoaderView = (props: LoaderViewProps) => {
  const [stagedOrderDetails, setStagedOrderDetails] = useState<StagedOrderDetails | undefined>(undefined);
  const [confirmLoadOpen, setConfirmLoadOpen] = useState(false);

  const loadingCompleted = useLoadingCompleted(stagedOrderDetails?.id ?? "", {
    onSuccess: () => {
      props.displayMessageSuccess(`Staged order for order number ${stagedOrderDetails?.orderNumber} successfully loaded`);
      setConfirmLoadOpen(false);
      setStagedOrderDetails(undefined);
      props.refreshStagedOrders();
    },
    onError: (err) => {
      setConfirmLoadOpen(false);
      props.displayMessageFail(adminFacingErrorMessage(err));
    }
  });

  const filteredSortedStagedOrders = props.stagedOrders
    .filter((so) => so.step !== StagedOrderStep.Gross && so.step !== StagedOrderStep.Signature && so.step !== StagedOrderStep.TicketInstructions && !so.manuallyLoaded)
    .sort(byPriorityThenByName);

  return <>
    {stagedOrderDetails && <StagedOrderDetailsModal
      stagedOrder={stagedOrderDetails}
      onClose={() => setStagedOrderDetails(undefined)}
      onLoad={() => setConfirmLoadOpen(true)} />}
    {confirmLoadOpen && stagedOrderDetails && <Confirm
      visible={confirmLoadOpen}
      title={"Load Staged Order"}
      body={`Are you sure you want to mark Staged Order for Order ${stagedOrderDetails.orderNumber} as loaded?`}
      confirmStyle='primary'
      loading={loadingCompleted.isLoading}
      onConfirm={() => loadingCompleted.request()}
      onDismiss={() => setConfirmLoadOpen(false)} />}
    <Table responsive className="fs-6 table-hover">
      <thead>
        <tr className="p-1 mb-3 bg-primary bg-opacity-10">
          <th>Order #</th>
          {props.selectedColumns.includes(SelectableColumns.ReleaseNumber) && <th>Release #</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <th>Driver Email</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <th>Driver #</th>}
          {props.selectedColumns.includes(SelectableColumns.DriverName) && <th>Driver Name</th>}
          {props.selectedColumns.includes(SelectableColumns.Transport) && <th>Transport</th>}
          {props.selectedColumns.includes(SelectableColumns.Customer) && <th>Customer</th>}
          {props.selectedColumns.includes(SelectableColumns.Products) && <th>Products</th>}
          {props.selectedColumns.includes(SelectableColumns.Amount) && <th>Amount</th>}
          {props.selectedColumns.includes(SelectableColumns.RequestedQuantity) && <th>Requested Quantity</th>}
          {props.selectedColumns.includes(SelectableColumns.TareWeight) && <th>Tare Weight</th>}
          {props.selectedColumns.includes(SelectableColumns.Status) && <th>Status</th>}
          {props.selectedColumns.includes(SelectableColumns.Bay) && <th>Bay</th>}
          {props.selectedColumns.includes(SelectableColumns.Destination) && <th>Destination</th>}
          {props.selectedColumns.includes(SelectableColumns.Created) && <th>Created</th>}
          <th style={{ width: '2rem' }} />
        </tr>
      </thead>
      <tbody>
        {filteredSortedStagedOrders.map(so =>
          <tr key={so.id}
            className="border-bottom border-1"
            role="button"
            onClick={() => setStagedOrderDetails(so)}>
            <LoaderCell>
              <span className="link-primary text-decoration-underline">{so.orderNumber}</span>
              {so.orderNumbers.length > 0 && <span className="text-muted small"> ({so.orderNumbers.length})</span>}
            </LoaderCell>
            {props.selectedColumns.includes(SelectableColumns.ReleaseNumber) && <LoaderCell>{so.releaseNumber ?? ""}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.DriverEmail) && <LoaderCell>{so.userEmail}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.DriverNumber) && <LoaderCell>{so.driverNumber}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.DriverName) && <LoaderCell>{so.driverName}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Transport) && <LoaderCell>{so.transport}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Customer) && <LoaderCell>{so.customers.join(", ")}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Products) && <LoaderCell>{so.products.join(", ")}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Amount) && <td className="text-nowrap">{so.availableToRequest.toLocaleString() + " lb"}</td>}
            {props.selectedColumns.includes(SelectableColumns.RequestedQuantity) && <td className="text-nowrap">{so.requestedQuantity.toLocaleString() + " lb"}</td>}
            {props.selectedColumns.includes(SelectableColumns.TareWeight) && <td className="text-nowrap">{so.tareWeight}</td>}
            {props.selectedColumns.includes(SelectableColumns.Status) && <LoaderCell>{formatStagedOrderStep(so.step)}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Bay) && <LoaderCell>{so.bay}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Destination) && <LoaderCell>{so.destinations.join(", ")}</LoaderCell>}
            {props.selectedColumns.includes(SelectableColumns.Created) && <LoaderCell>{new Date(so.createdAt).toLocaleDateString()}</LoaderCell>}
            <td className="text-end align-middle text-secondary">
              <i className="bi-chevron-right fs-5" />
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  </>
}

const LoaderCell = (props: { children: ReactNode }) => <td><p className="line-clamp line-clamp-2 mb-0">{props.children}</p></td>;

const formatStagedOrderStep = (step: StagedOrderStep) =>
  <Badge className={`bg-stagedOrderStep-${step}`} style={{ textWrap: "wrap" }}>{step.replace(/([A-Z])/g, ' $1').trim()}</Badge>;

const alphabeticallyByName = (a: StagedOrderDetails, b: StagedOrderDetails) => a.orderNumber.localeCompare(b.orderNumber);

const byPriorityThenByName = (a: StagedOrderDetails, b: StagedOrderDetails) =>
  Object.values(StagedOrderStep).indexOf(b.step) - Object.values(StagedOrderStep).indexOf(a.step) || a.orderNumber.localeCompare(b.orderNumber);

export { StagedOrders };
